/**
 * ══ PRACTICE MODE — a walkthrough that leaves NOTHING BEHIND ══════════════════════════════════════
 *
 * The owner's requirement, in his words: "While it takes you through and you are essentially
 * completing a task for instance submitting a daily report. I want the user to physically go through
 * all the steps and click all the buttons. However, at the end the record they just created does not
 * need to exist."
 *
 * So: every real screen, every real button, every real click — and nothing durable at the end. Not
 * "cleaned up afterwards". Nothing durable AT ANY POINT, because a daily report that reaches submit
 * has already minted field tickets, time entries and posted cost against a real job, and a sticker
 * that reaches create has already burned a code and glued a dead label onto a real machine. There is
 * no window in which those are safe to exist.
 *
 * THE MECHANISM: while a practice run is armed, no mutating request leaves the browser. Reads all go
 * through untouched, so the person is looking at real screens with real data in real dropdowns.
 * Writes are answered here with a realistic response — including a synthesised id, so the screen
 * that comes next still works — and never reach the network.
 *
 * ── WHY THE PATCH IS AT THE TRANSPORT AND NOT IN "THE API CLIENT" ────────────────────────────────
 * There is no such thing as "the" API client in this app. There are SEVEN axios instances built by
 * five different factories (shared/api/client.js plus raw axios.create in api/index.js,
 * api/notifications.js, modules/timekeeping/api/timekeeping.js — three of them in that one file —
 * modules/projects/api/projects.js, modules/finance/api/finance.js, modules/hr/services/api.js) and
 * 23 places that call bare fetch(), including the two that matter most here: PublicInspection.jsx,
 * which is the screen a scanned sticker opens, and FieldInput.jsx, which PUTs an attachment straight
 * to S3 on a presigned URL. An interceptor bolted onto the shared factory would have covered NONE of
 * the daily-report tour and none of the scanned-sticker flow.
 *
 * Worse, it could not be made airtight later either: axios snapshots its adapter into each instance
 * at axios.create() time, and every one of those instances is built at module import — before any
 * tour can start. So there is no runtime hook that reaches all of them.
 *
 * XMLHttpRequest and fetch are the only two doors out of the browser, axios 1.10 goes through the
 * xhr adapter in every environment this app runs in (adapter order 'xhr','http','fetch' —
 * node_modules/axios/lib/defaults/index.js:40), and patching those two is therefore the only place
 * "zero" is a fact about the mechanism rather than a claim about an inventory of call sites that
 * grows every sprint. __tests__/practiceMode.test.js is what holds that claim honest.
 *
 * ── ALTERNATIVES CONSIDERED AND REJECTED ─────────────────────────────────────────────────────────
 * 1. A SERVER-SIDE `is_practice` FLAG ON REAL ROWS. Rejected, and it is the most dangerous of the
 *    three. Every list, total, gate, export, invoice, report and dashboard in fifteen modules would
 *    then have to remember to exclude it — and this codebase's single most repeated defect is one
 *    query that forgot a filter (the master-timesheet site filter, the lump-sum jobs that reported
 *    $0 billed forever, four fleet pages rendering empty on an envelope key). A practice daily report
 *    that slipped into a billing total is the worst outcome available in this whole batch, and the
 *    flag design makes that outcome one forgotten WHERE clause away, forever, in code nobody
 *    remembers is load-bearing for a training feature.
 * 2. CREATE THEN DELETE. Rejected on two counts. The window between the two is real — a report is
 *    visible to an approver, a notification is dispatched, an email is sent, an S3 object exists, a
 *    sequential ticket number is consumed from an advisory-lock singleton and CANNOT be given back
 *    (gaps in a billing sequence are gaps somebody has to explain). And the cleanup does not run when
 *    the user closes the tab, which is exactly how a training session ends.
 * 3. A TRANSACTION ROLLED BACK PER REQUEST. Rejected because the flow does not fit in one request: a
 *    draft is saved, then edited, then submitted, and each is its own HTTP call with its own
 *    transaction. Per-request rollback cannot hold a multi-request flow together, and a flow that
 *    half-committed is worse than either extreme.
 *
 * ── THE ONE THING THAT MUST STILL GO OUT ─────────────────────────────────────────────────────────
 * POST /auth/refresh. It is the session, not the user's work. Blocking it means the access token
 * expires mid-tour, every subsequent read 401s, and the app bounces the trainee to /login — a dead
 * end produced by the safety mechanism itself. Same for logout: a person must always be able to
 * leave. That list is deliberately tiny and explicit (SESSION_PATHS below); it is not a general
 * escape hatch and nothing should be added to it that writes tenant data.
 *
 * ── STOPPING THE WRITE IS ONLY HALF OF IT ────────────────────────────────────────────────────────
 * A blocked write is answered with a synthesised success, and the screen that receives it stores that
 * answer as though it were server truth. Un-patching the transports at the end of a run does not
 * un-say any of that. Reported from staging, with a screenshot: the end-of-run card read "Nothing you
 * just did was saved… Practice mode is now off. Anything you do from here is real", and the panel
 * BEHIND it still read Badge Number 1001 / PIN Issued / Issued 08/10/2026 6:46 AM — with Reset PIN
 * and Revoke beside it, newly live. The card and the screen contradicted each other, and the screen is
 * the one people believe. The failure that produces is specific: press Revoke on a PIN that does not
 * exist and the request is REAL — best case a confusing error, worst case a hand who really did hold a
 * PIN has it revoked out from under them at a machine this morning.
 *
 * So disarming BROADCASTS (subscribePracticeEnded below). `armed` is cleared and the transports are
 * put back BEFORE any listener runs, which is what makes it safe for a listener to answer by
 * RE-READING FROM THE SERVER — the strictly better reset, because it replaces fiction with the truth
 * rather than with a blank. Order the other way round and the refetch would itself be intercepted, and
 * the screen would be "put back" to a second fabrication.
 *
 * ── AND IT WAITS FOR THE ANSWER IT IS UNDOING ───────────────────────────────────────────────────
 * A blocked write is answered ONE MACROTASK LATER (`answerXhr` below schedules the events, because a
 * real XHR never calls back inside send()). A step gated `until: { requested: … }` is satisfied by
 * the EMIT, which is synchronous. So when such a step is the LAST one, the whole chain — gate,
 * next(), endTour, disarmPractice — runs BEFORE the synthesised 201 comes back, and the screen's
 * success handler then rebuilt the fabrication on top of a reset that had already finished. Measured
 * on the flagship tour, whose final step is exactly that shape: after the run, the daily report was
 * back to holding `practice-1` and the foreman's next real save PUT a report that never existed.
 *
 * The broadcast therefore waits until every answer this run handed out has been DELIVERED and the
 * callers' promise continuations have run (see `answerDelivered`). It is still synchronous when
 * nothing is in flight, which is the ordinary case and keeps the disarm, the screens' reset and the
 * end-of-run card in one React batch.
 *
 * ── AND IT ONLY FIRES FOR A RUN THAT ACTUALLY FABRICATED SOMETHING ──────────────────────────────
 * Every fabrication comes from a blocked write, so a run with `blockedCount === 0` has left nothing
 * on any screen and has nothing to undo. Broadcasting anyway is not free: these resets are BLUNT by
 * design — FieldAccessPanel's clears the badge and PIN boxes outright — and "Show me how" followed a
 * second later by Escape would then wipe a credential the admin had typed BEFORE the run, which no
 * undo on that panel can give back. So a run that stopped nothing tells nobody.
 *
 * ── FAIL-SAFE, NOT FAIL-OPEN ─────────────────────────────────────────────────────────────────────
 * The wrappers consult `armed` at CALL time, not at install time. So a wrapper that somehow outlives
 * disarm() — another library patched fetch on top of ours and we could not restore cleanly — is
 * INERT rather than blocking: it passes everything straight through. The failure mode of this file
 * is "practice mode stopped protecting", which a test catches, and never "the app stopped working",
 * which a user catches.
 */

/** Methods that cannot change anything on the server, and therefore always go out. */
const READ_ONLY = new Set(['GET', 'HEAD', 'OPTIONS'])

/**
 * The session plumbing, which is not the user's work and must survive a practice run. Matched
 * against the URL's PATH so a query string can never smuggle something past.
 */
const SESSION_PATHS = ['/auth/refresh', '/auth/logout', '/auth/login']

/** true while a practice run owns the transports. Read at call time — see the header. */
let armed = false
let installed = false

let realFetch = null
let realOpen = null
let realSend = null

let synthCount = 0
/** Mutating requests stopped during the CURRENT run. Shown to the user at the end. */
let blockedCount = 0
/** Moves once per armed run — see practiceRunNumber. */
let runNumber = 0

/**
 * Synthesised answers handed out but whose CALLER may not have run yet — see `answerDelivered`.
 * Not reset by arm/disarm: a stale timer from a finished run still decrements this when it fires,
 * and zeroing it here would drive the count negative and wedge the next run's broadcast.
 */
let answersInFlight = 0
/** A disarm is waiting for the count above to reach zero before it tells the screens. */
let endBroadcastPending = false
/**
 * WHICH pending broadcast a deferred flush was scheduled for.
 *
 * Bumped every time a broadcast becomes pending, and carried by every timer that might flush
 * it. A timer whose epoch is no longer the current one belongs to a run that has already been
 * reset, and does nothing.
 *
 * WHY IT EXISTS (2026-09-17, the flake that blocked two unrelated deploys since 20 August).
 * `disarmPractice` arms a 250 ms fail-safe whenever it ends a run with an answer still in
 * flight. When the answer lands first — the normal case — the pending flag is cleared and that
 * timer is left to fire into nothing. But the flag is a single boolean shared by every run, so
 * a stale timer firing a quarter of a second later would find the NEXT run's freshly-set flag
 * and broadcast ITS reset early: the screens are told the run ended while that run's own answer
 * is still on its way, the success handler then rebuilds the fabrication on top of a finished
 * reset, and a practice row is left in a real daily report. That is the exact failure the
 * ordering test guards, and it is why the test failed about one run in three: two practice runs
 * inside 250 ms of each other are ordinary in a test file, and reachable by a person who ends
 * one tour and starts another.
 */
let endBroadcastEpoch = 0

const requestListeners = new Set()
/** Screens that have to drop what a blocked write put on them. See subscribePracticeEnded. */
const endListeners = new Set()
const responders = []

/**
 * The prefix every synthesised id carries, exported so a screen can RECOGNISE one it is still
 * holding rather than having to be told a run happened.
 *
 * That recognition is what lets the daily report take the narrow reset it needs: clearing an id it
 * can prove is fictional, instead of re-reading the whole report and throwing away a day of crew
 * hours the foreman has typed and not saved.
 */
export const PRACTICE_ID_PREFIX = 'practice-'

/** Is this id one this module invented? */
export const isPracticeId = (value) =>
  typeof value === 'string' && value.startsWith(PRACTICE_ID_PREFIX)

// ── what a practice request is answered with ─────────────────────────────────────────────────────

/**
 * Teach practice mode the realistic shape of one endpoint's response.
 *
 * The default answer (below) echoes the request body back with a synthesised id, which is what most
 * of this app's create endpoints actually return and is enough for the next screen to work. It is
 * NOT enough where the server computes something the client could not: a sticker's `code` and
 * `url_path` are minted server-side, so a practice sticker answered by the default would print a
 * blank label — the tour would "work" and teach the wrong thing.
 *
 * @param {(method: string, path: string) => boolean} match
 * @param {(req: {method: string, url: string, path: string, body: any}) => any} build
 * @returns {() => void} unregister
 */
export function registerPracticeResponse(match, build) {
  const entry = { match, build }
  responders.push(entry)
  return () => {
    const i = responders.indexOf(entry)
    if (i >= 0) responders.splice(i, 1)
  }
}

/** Test seam: forget every registered responder. */
export function clearPracticeResponses() {
  responders.length = 0
}

/**
 * AN HONEST "NO" FROM A PRACTICE RUN.
 *
 * The generic answer to a blocked write is a success, and for a record that exists only inside the run
 * (registerPracticeRead) that is a lie wherever the run does not model the write: the screen says
 * "saved", re-reads, and the change is not there — or worse, it keeps a fabricated row the next read
 * contradicts. A responder that cannot model a write answers with this instead, and the screen shows
 * its own refusal path with the sentence given. Reads may answer with one too ("the practice project
 * has only its cost report") rather than letting a practice address reach the server.
 *
 * 409, because every screen here prints a 409's `detail` as it stands (estimateErrorMessage, the
 * module's errorMessage) and none treats it as a sign-out (401) or a missing permission (403).
 */
const REFUSAL = Symbol.for('tynvara.practiceRefusal')

export function practiceRefusal(message, status = 409) {
  return { [REFUSAL]: true, status, body: { detail: String(message) } }
}

export const isPracticeRefusal = (answer) => !!(answer && typeof answer === 'object' && answer[REFUSAL])

/** A built answer as (payload, status): a refusal carries its own status. */
function asResponse(answer, status) {
  return isPracticeRefusal(answer) ? { payload: answer.body, status: answer.status } : { payload: answer, status }
}

// ── practice READS — a record that exists only inside a practice run ─────────────────────────────

/**
 * ══ WHY A PRACTICE RUN MAY ANSWER A READ ════════════════════════════════════════════════════════
 *
 * Everything above is about WRITES: they never leave, and reads always do. That is enough for every
 * tour that walks somebody through making something new — the daily report, the sticker, the PIN —
 * because the screen it starts on is real and the thing it makes is answered here.
 *
 * It is not enough for a tour that teaches somebody to WORK a record that already has a history: a
 * capital estimate with its systems, crews, schedule, price, proposal PDF and signatures, walked
 * through to its award. There is no honest way to build that from blocked writes (the first one would
 * be "create the estimate", and every read after it would ask the server for an id it never issued),
 * and the two server-side ways to have one are the two this file already rejects: a seeded row with
 * an `is_practice` flag every board, total, report and export would have to remember, or a real row
 * created and deleted around the run.
 *
 * So a practice tour may register READS for a record that lives only inside the run. The rule that
 * keeps this from ever shadowing real data is the caller's, and it is the same rule every responder
 * already follows: match ONLY ids that cannot exist on the server (a capital estimate's id is a
 * positive serial; the practice one is negative), and answer `undefined` for anything else so it goes
 * out as before.
 *
 * WHAT THIS DOES NOT CHANGE: the write rule. A read answered here is still counted as a READ — it
 * never reaches the blocked count the end-of-run card states, and the write guard above it is
 * untouched. Outside a run nothing here is consulted at all (`armed` is checked first), so a practice
 * id asked for after the run is a real request the server refuses, which is the honest answer.
 */
const readResponders = []

/**
 * Teach practice mode to answer a READ locally during a run.
 *
 * @param {(method: string, path: string) => boolean} match
 * @param {(req: {method: string, url: string, path: string, query: URLSearchParams}) => any} build
 *        the response body; `undefined` lets the request go out after all.
 * @returns {() => void} unregister
 */
export function registerPracticeRead(match, build) {
  const entry = { match, build }
  readResponders.push(entry)
  return () => {
    const i = readResponders.indexOf(entry)
    if (i >= 0) readResponders.splice(i, 1)
  }
}

/** Test seam: forget every registered read. */
export function clearPracticeReads() {
  readResponders.length = 0
}

function queryOf(url) {
  try {
    const base = typeof window !== 'undefined' && window.location ? window.location.href : 'http://localhost/'
    return new URL(String(url || ''), base).searchParams
  } catch {
    return new URLSearchParams('')
  }
}

/** The local answer to a read during a run, or `undefined` when it should go out. */
function practiceReadFor(method, url) {
  if (!armed || !READ_ONLY.has(method) || method === 'OPTIONS') return undefined
  const path = pathOf(url)
  for (const { match, build } of readResponders) {
    let hit = false
    try { hit = !!match(method, path) } catch { hit = false }
    if (!hit) continue
    let built
    try { built = build({ method, url, path, query: queryOf(url) }) } catch { built = undefined }
    if (built !== undefined) return built
  }
  return undefined
}

function pathOf(url) {
  const raw = String(url || '')
  try {
    // A relative URL ('/api/forms/…', which is what every call in this app uses) needs a base.
    const base = typeof window !== 'undefined' && window.location ? window.location.href : 'http://localhost/'
    return new URL(raw, base).pathname
  } catch {
    // Malformed enough that we cannot read a path. Treat the whole string as one — a mutating
    // request we cannot classify is one we BLOCK, never one we wave through.
    return raw.split('?')[0]
  }
}

function readBody(body) {
  if (body == null) return null
  if (typeof body === 'string') {
    try { return JSON.parse(body) } catch { return null }
  }
  // FormData / Blob / ArrayBuffer — a file upload. There is nothing to echo, and that is fine:
  // the default answer still carries an id, which is what the caller reads.
  return null
}

function defaultAnswer(method, path, body) {
  if (method === 'DELETE') return {}
  synthCount += 1
  const now = new Date().toISOString()
  const echoed = body && typeof body === 'object' && !Array.isArray(body) ? body : {}
  return {
    ...echoed,
    // A synthesised id so the screen that comes next still works. Deliberately NOT uuid-shaped: if
    // one of these ever turns up in a log, a database or a bug report, it must be instantly
    // identifiable as something that never existed.
    id: echoed.id != null ? echoed.id : `practice-${synthCount}`,
    practice: true,
    created_at: now,
    updated_at: now,
  }
}

function answerFor(method, url) {
  const path = pathOf(url)
  return (body) => {
    for (const { match, build } of responders) {
      let hit = false
      try { hit = !!match(method, path) } catch { hit = false }
      if (!hit) continue
      let built
      try { built = build({ method, url, path, body }) } catch { built = undefined }
      if (built !== undefined) return built
    }
    return defaultAnswer(method, path, body)
  }
}

// ── the request bus (also what the `requested` step gate listens to) ─────────────────────────────

/**
 * @param {(req: {method: string, url: string, path: string, blocked: boolean}) => void} fn
 * @returns {() => void} unsubscribe
 */
export function subscribePracticeRequests(fn) {
  requestListeners.add(fn)
  return () => requestListeners.delete(fn)
}

/**
 * Be told when a practice run ENDS, so a screen can drop the state a blocked write produced.
 *
 * Every listener is called AFTER `armed` is false and the transports are back, so a listener may —
 * and should — answer by re-reading from the server. Use `usePracticeReset` from a component rather
 * than calling this directly; it owns the unsubscribe.
 *
 * A listener that throws is swallowed: one screen's reset must never stop the others', and the whole
 * point of this seam is that no screen is left asserting something untrue.
 *
 * @param {() => void} fn
 * @returns {() => void} unsubscribe
 */
export function subscribePracticeEnded(fn) {
  endListeners.add(fn)
  return () => endListeners.delete(fn)
}

/**
 * Test seam: how many screens are currently listening.
 *
 * Exported so the guard in __tests__/tourWalkthrough.test.jsx can MOUNT each screen a practice tour
 * writes on and prove that mounting it subscribed — rather than grepping for a hook call, which is a
 * claim about the source rather than about the tree the tour actually walks onto.
 */
export const practiceEndSubscriberCount = () => endListeners.size

/**
 * Tell every screen the run is over.
 *
 * The listener set is SNAPSHOTTED first, which is what makes it safe for a listener to unsubscribe
 * (or to disarm re-entrantly) from inside its own callback. The cost of the snapshot is that a
 * listener which SUBSCRIBES during the broadcast is not told — a screen mounted as a consequence of
 * another screen's reset. Nothing does that today (no reset here mounts anything), and it is written
 * down because the snapshot's two properties get read together by whoever touches this next.
 */
function broadcastEnded() {
  for (const fn of Array.from(endListeners)) {
    try { fn() } catch { /* one screen's reset must not stop another's */ }
  }
}

/**
 * Idempotent: whichever of the two paths in `answerDelivered` arrives first wins.
 *
 * `epoch` says which pending broadcast the caller meant. A deferred flush passes the epoch it
 * was scheduled for and does nothing once that broadcast has already gone out — see
 * `endBroadcastEpoch`. A caller with no epoch (`armPractice`, `disarmPractice`) means "whatever
 * is pending now", which is exactly right for them.
 */
function flushEndedBroadcast(epoch) {
  if (epoch !== undefined && epoch !== endBroadcastEpoch) return
  if (!endBroadcastPending) return
  endBroadcastPending = false
  broadcastEnded()
}

/** An answer has been built and is about to be handed to a caller. */
function answerScheduled() { answersInFlight += 1 }

/**
 * An answer has reached its caller.
 *
 * The caller's own continuation — the `.then` inside axios, and then the component's success handler
 * — is a MICROTASK queued by that delivery, and the microtask queue drains completely before the
 * next macrotask. So one more `setTimeout(…, 0)` is the first moment at which every screen has
 * finished acting on this answer, and therefore the first moment at which a reset can be sure it is
 * cleaning up after the fabrication rather than being overwritten by it.
 */
function answerDelivered() {
  answersInFlight -= 1
  if (answersInFlight > 0 || !endBroadcastPending) return
  const epoch = endBroadcastEpoch
  setTimeout(() => flushEndedBroadcast(epoch), 0)
}

/**
 * Announce a request. Called by the wrappers below, and exported so a test — or a future non-practice
 * tour that still wants a `requested` gate — can drive the same bus without a transport.
 */
export function emitPracticeRequest(req) {
  const event = { method: 'GET', url: '', blocked: false, ...req }
  if (!event.path) event.path = pathOf(event.url)
  for (const fn of Array.from(requestListeners)) {
    try { fn(event) } catch { /* a listener must never break the request it is watching */ }
  }
}

// ── the rule ─────────────────────────────────────────────────────────────────────────────────────

/** Does this request go out? */
export function passesThrough(method, url) {
  const verb = String(method || 'GET').toUpperCase()
  if (READ_ONLY.has(verb)) return true
  const path = pathOf(url)
  return SESSION_PATHS.some((p) => path.endsWith(p) || path.includes(`${p}/`) || path.includes(`${p}?`))
}

// ── fetch ────────────────────────────────────────────────────────────────────────────────────────

function jsonResponse(payload, status) {
  const text = JSON.stringify(payload)
  if (typeof Response === 'function') {
    return new Response(text, { status, headers: { 'Content-Type': 'application/json' } })
  }
  // jsdom does not ship fetch, so Response may be absent depending on the host. A duck of the parts
  // this app's fetch callers actually read (`res.ok`, `res.status`, `res.json()`, `res.text()`).
  return {
    ok: status >= 200 && status < 300,
    status,
    statusText: 'OK',
    headers: { get: (n) => (String(n).toLowerCase() === 'content-type' ? 'application/json' : null) },
    json: async () => JSON.parse(text),
    text: async () => text,
  }
}

function practiceFetch(input, init) {
  const method = String(
    (init && init.method) || (input && typeof input === 'object' && input.method) || 'GET',
  ).toUpperCase()
  const url = typeof input === 'string' ? input : (input && input.url) || String(input)

  const localRead = practiceReadFor(method, url)
  if (localRead !== undefined) {
    // A record that exists only inside this run (registerPracticeRead). Delivered a macrotask later
    // and counted in flight like a blocked write's answer, so a disarm waits for it the same way.
    answerScheduled()
    setTimeout(answerDelivered, 0)
    emitPracticeRequest({ method, url, blocked: false, practiceRead: true })
    const read = asResponse(localRead, 200)
    return Promise.resolve(jsonResponse(read.payload, read.status))
  }

  if (!armed || passesThrough(method, url)) {
    emitPracticeRequest({ method, url, blocked: false })
    return realFetch.call(typeof window !== 'undefined' ? window : globalThis, input, init)
  }

  blockedCount += 1
  const body = readBody(init && init.body)
  const answered = asResponse(answerFor(method, url)(body), method === 'DELETE' ? 200 : 201)
  // COUNTED BEFORE THE EMIT, and that order is the whole point: the emit can satisfy a step's gate,
  // end the tour and reach disarmPractice synchronously, and the disarm has to be able to see that
  // an answer is still on its way. The promise below is already resolved, so the caller's handler is
  // a microtask — one macrotask is enough to be after it.
  answerScheduled()
  setTimeout(answerDelivered, 0)
  emitPracticeRequest({ method, url, blocked: true })
  return Promise.resolve(jsonResponse(answered.payload, answered.status))
}

// ── XMLHttpRequest (i.e. every axios call in the app) ────────────────────────────────────────────

const own = (xhr, name, value) => {
  try {
    Object.defineProperty(xhr, name, { value, writable: true, configurable: true, enumerable: false })
  } catch { /* a host that will not let us shadow it; the fallbacks below still answer */ }
}

/**
 * Hand an already-opened XHR a synthesised success WITHOUT sending it.
 *
 * `status`/`responseText`/`readyState` are prototype getters, so they are shadowed with own
 * properties; jsdom's internal state stays at OPENED and nothing ever touches the socket. axios
 * reads exactly `status`, `statusText`, `responseText` (or `response` for a non-text responseType)
 * and `getAllResponseHeaders()` in its onloadend (axios/lib/adapters/xhr.js:40-56), and it installs
 * that handler as `request.onloadend` — an IDL event handler, so dispatching the event invokes it.
 */
function answerXhr(xhr, method, url, body) {
  const answered = asResponse(answerFor(method, url)(readBody(body)), method === 'DELETE' ? 200 : 201)
  deliverXhr(xhr, url, answered.payload, answered.status)
}

/** Hand an opened XHR `payload` as a finished response — a blocked write's answer or a practice read. */
function deliverXhr(xhr, url, payload, status) {
  const text = JSON.stringify(payload)

  own(xhr, 'readyState', 4)
  own(xhr, 'status', status)
  own(xhr, 'statusText', status === 201 ? 'Created' : 'OK')
  own(xhr, 'responseText', text)
  // A caller that asked for a FILE (a PDF preview or download) is handed one: its error path reads the
  // refusal's sentence back out of a Blob (proposal.ts readBlobError), and a string would hide it.
  const asBlob = xhr.responseType === 'blob' && typeof Blob === 'function'
  own(xhr, 'response', xhr.responseType === 'json' ? payload : asBlob ? new Blob([text], { type: 'application/json' }) : text)
  own(xhr, 'responseURL', String(url || ''))
  own(xhr, 'getAllResponseHeaders', () => 'content-type: application/json\r\n')
  own(xhr, 'getResponseHeader', (n) => (String(n).toLowerCase() === 'content-type' ? 'application/json' : null))

  // Asynchronously, because a real XHR never calls back inside send() and a caller that assumed it
  // could would be a caller this shim broke rather than protected. THAT GAP IS ALSO THE ONE A DISARM
  // has to wait out — see the header — so the answer is counted from here until it lands.
  answerScheduled()
  setTimeout(() => {
    try {
      for (const type of ['readystatechange', 'load', 'loadend']) {
        let delivered = false
        try {
          xhr.dispatchEvent(new Event(type))
          delivered = true
        } catch { /* fall through to the direct call below */ }
        if (delivered) continue
        const handler = xhr[`on${type}`]
        if (typeof handler === 'function') {
          try { handler.call(xhr, { type, target: xhr }) } catch { /* the caller's own problem */ }
        }
      }
    } finally {
      // ONE MORE MACROTASK, and this is the whole fix for a flake that has blocked unrelated
      // deploys since v2.32.0.
      //
      // `dispatchEvent` runs the caller's XHR handler SYNCHRONOUSLY, which resolves the promise the
      // caller is awaiting — but the caller's own `.then` is a MICROTASK, so it has not run yet.
      // Calling answerDelivered() here, in the same macrotask, dropped answersInFlight to zero and
      // broadcast "practice ended" BEFORE the answer reached the caller: the reset arrived first and
      // cleaned up a thing that did not exist yet.
      //
      // The fetch path above already does exactly this and says why — "the promise below is already
      // resolved, so the caller's handler is a microtask; one macrotask is enough to be after it."
      // The two transports now wait the same gap.
      setTimeout(answerDelivered, 0)
    }
  }, 0)
}

function practiceOpen(method, url, ...rest) {
  this.__practiceMethod = String(method || 'GET').toUpperCase()
  this.__practiceUrl = String(url || '')
  return realOpen.call(this, method, url, ...rest)
}

function practiceSend(body) {
  const method = this.__practiceMethod || 'GET'
  const url = this.__practiceUrl || ''
  const localRead = practiceReadFor(method, url)
  if (localRead !== undefined) {
    // A record that exists only inside this run — see registerPracticeRead. Never sent, never
    // counted as a blocked write.
    const read = asResponse(localRead, 200)
    deliverXhr(this, url, read.payload, read.status)
    emitPracticeRequest({ method, url, blocked: false, practiceRead: true })
    return undefined
  }
  if (!armed || passesThrough(method, url)) {
    emitPracticeRequest({ method, url, blocked: false })
    return realSend.call(this, body)
  }
  blockedCount += 1
  answerXhr(this, method, url, body)
  emitPracticeRequest({ method, url, blocked: true })
  return undefined
}

// ── arm / disarm ─────────────────────────────────────────────────────────────────────────────────

function install() {
  if (installed) return
  const g = typeof window !== 'undefined' ? window : globalThis

  // `g.fetch === practiceFetch` means a PREVIOUS run's wrapper is still in place because uninstall
  // could not restore it (see below). Wrapping it again would make practiceFetch call itself
  // forever. Leave it, keep the realFetch it already closed over, and simply re-arm.
  if (typeof g.fetch === 'function' && g.fetch !== practiceFetch) {
    realFetch = g.fetch
    g.fetch = practiceFetch
  }
  if (typeof g.XMLHttpRequest === 'function') {
    const proto = g.XMLHttpRequest.prototype
    if (proto.open !== practiceOpen) {
      realOpen = proto.open
      proto.open = practiceOpen
    }
    if (proto.send !== practiceSend) {
      realSend = proto.send
      proto.send = practiceSend
    }
  }
  installed = true
}

function uninstall() {
  if (!installed) return
  const g = typeof window !== 'undefined' ? window : globalThis

  // Restore ONLY what is still ours. If something else has patched on top of us since, putting the
  // original back would silently delete their wrapper — so we leave theirs in place and rely on
  // `armed === false` making ours inert. Loud in dev, because it means two things are fighting over
  // the transport and somebody should know.
  if (realFetch && g.fetch === practiceFetch) g.fetch = realFetch
  else if (realFetch && g.fetch !== realFetch) {
    console.warn('[tour] practice mode could not restore window.fetch — something patched it on top. It is inert, not blocking.')
  }

  if (g.XMLHttpRequest) {
    if (realOpen && g.XMLHttpRequest.prototype.open === practiceOpen) g.XMLHttpRequest.prototype.open = realOpen
    if (realSend && g.XMLHttpRequest.prototype.send === practiceSend) g.XMLHttpRequest.prototype.send = realSend
    else if (realSend && g.XMLHttpRequest.prototype.send !== realSend) {
      console.warn('[tour] practice mode could not restore XMLHttpRequest.send — something patched it on top. It is inert, not blocking.')
    }
  }

  // NOT nulled, and that is the whole of the fail-safe promise. A wrapper we could not unhook is
  // still out there and will still be called; it passes the request through to whatever it wrapped,
  // and `realFetch`/`realSend` ARE that. Clearing them here turned a leaked wrapper from harmless
  // into a TypeError on every request for the rest of the session — which is exactly the failure
  // mode this file is not allowed to have, and is what
  // __tests__/practiceMode.test.jsx › "is INERT rather than blocking" caught.
  installed = false
}

/**
 * Start a practice run. Returns the function that ends it — call it from an effect cleanup so that
 * unmounting, logging out, abandoning the tour and navigating away all disarm by the same path.
 *
 * Re-arming while already armed is a no-op that returns the SAME disarm, so two callers cannot leave
 * one half-installed patch behind.
 */
// ── the run's beginning and end, for whatever cached what a run answered ──────────────────────────

/**
 * EVERY ARM AND EVERY DISARM, whatever the run did — unlike subscribePracticeEnded, which is told only
 * when a run blocked a write. A practice READ leaves its answers in a query cache whether or not
 * anything was written, and a cache outlives the screen that filled it: a run that ended with Project
 * Financials unmounted left the practice estimate cached for five minutes, under its own key, ready to
 * be shown to the next run (review of 17f785bc, F2). So whoever owns a cache is told at both ends.
 */
const runListeners = new Set()
export function subscribePracticeRuns(fn) {
  runListeners.add(fn)
  return () => runListeners.delete(fn)
}
function announceRun(armedNow) {
  for (const fn of Array.from(runListeners)) {
    try { fn({ armed: armedNow }) } catch { /* one listener must not stop another */ }
  }
}

/**
 * What to drop from the app's query cache at each end of a run — registered by whatever answers
 * practice reads (practice/capexEstimate.js), called by TourProvider, which holds the app's client.
 * `purge(queryClient, { armed, late })`; `late` is the second pass after a disarm, once the screens the
 * run ended on have had a render to leave.
 */
const queryPurges = []
export function registerPracticeQueryPurge(purge) {
  queryPurges.push(purge)
  return () => {
    const i = queryPurges.indexOf(purge)
    if (i >= 0) queryPurges.splice(i, 1)
  }
}
export function purgePracticeQueries(queryClient, event) {
  if (!queryClient) return
  for (const purge of queryPurges) {
    try { purge(queryClient, event) } catch { /* a purge that throws must not stop the others */ }
  }
}

export function armPractice() {
  if (armed) return disarmPractice
  // A previous run's reset that is still waiting on an answer has to reach the screens BEFORE the
  // next run starts. Left pending, it would land mid-run — with the transports armed again, so its
  // re-reads would be answered by the mechanism they exist to undo.
  flushEndedBroadcast()
  blockedCount = 0
  runNumber += 1
  install()
  armed = true
  announceRun(true)
  return disarmPractice
}

export function disarmPractice() {
  if (!armed) return
  // ORDER IS THE WHOLE THING. `armed` goes false and the transports go back FIRST, so every request a
  // listener makes below is a real one. A broadcast before this line would have the re-reads
  // intercepted by the very mechanism they exist to undo.
  //
  // Guarded so this is idempotent: a second disarm returns at the line above, so no screen is ever
  // told twice about one run — which matters, because "tell the screens" means "issue requests".
  armed = false
  uninstall()
  // Before the blocked-count return below: a run that only READ still cached what it read.
  announceRun(false)

  // Nothing was stopped, so nothing was fabricated, so there is nothing to undo — and these resets
  // are blunt enough that running one for free costs a real half-typed credential. See the header.
  if (blockedCount === 0) return

  if (answersInFlight > 0) {
    endBroadcastPending = true
    const epoch = (endBroadcastEpoch += 1)
    // FAIL-SAFE, and it is the same principle as the wrappers': the failure mode of this file may be
    // "the reset came late", never "the reset never came". If a delivery timer is dropped — a
    // suspended tab, a host that culls timers — this still tells the screens. flushEndedBroadcast is
    // idempotent, so whichever of the two paths arrives first wins.
    setTimeout(() => flushEndedBroadcast(epoch), 250)
    return
  }
  broadcastEnded()
}

export const isPracticeArmed = () => armed

/**
 * Which practice run this is — a number that moves every time one is armed, and never otherwise.
 *
 * For state a practice READ answers from (registerPracticeRead): a run must start from the record as
 * it was before ANY run touched it, whether or not the screen that would have reset it was on screen
 * when the last run ended. Keying that state on this number makes "a new run starts clean" a property
 * of the store rather than of which component happened to be mounted.
 */
export const practiceRunNumber = () => runNumber

/** How many writes this run has stopped. The end-of-tour card states this to the user. */
export const practiceBlockedCount = () => blockedCount
